import { useMemo } from "react";
import { useNavigate } from "react-router";
import { ArrowRightLeft, Globe2, Layers } from "lucide-react";
import { authorityText, formatDate, useLocale, useTranslation } from "@ncp/i18n";
import { Badge, Card, CardContent, CardHeader, CardTitle, EmptyState, Skeleton, StatusBadge, cn, InfoHint, SourceText } from "@ncp/ui";
import { fmtAed, useJurisdictions, useSectors, useTransactions, type SectorRow } from "./api";

const FCSC = /^FCSC (\d{4}): output AED ([\d.]+)bn, price index ([\d.]+), growth (-?[\d.]+)%, ([\d,]+) establishments$/;
/** The sector note the statistics sync writes ("FCSC 2025: output AED 8.9bn, …"), in the reader's language; other notes as recorded. */
function SectorNote({ notes }: { notes: string }) {
  const { locale } = useLocale();
  const m = locale === "ar" ? notes.match(FCSC) : null;
  if (m) return <>{`المركز الاتحادي للتنافسية والإحصاء (${m[1]}): الإنتاج ${m[2]} مليار درهم، ومؤشر الأسعار ${m[3]}، والنمو ${m[4]}%، والمنشآت العاملة ${m[5]}`}</>;
  return <SourceText text={notes} />;
}

/** ISIC sector tree with regulator flags, UAE jurisdictions and the transaction register derived from notifications and agreements. */
/** Outcomes the reference feeds record as free text, mapped to their labels. */
const OUTCOME_KEYS: Record<string, string> = {
  "Cleared with conditions": "clearedWithConditions",
  "Completed (notified)": "completedNotified",
  Completed: "completed",
  Lapsed: "lapsed",
  "Notified – exemption review": "notifiedExemption",
  "Not notified – below the thresholds of Cabinet Decision No. 3 of 2025": "belowThresholds",
};

export function ReferenceTab() {
  const { t } = useTranslation();
  const transactionTypeLabel = (x: string) => {
    const agreement = /^Agreement \((.+)\)$/.exec(x);
    if (agreement) return t("backoffice:dataBank.agreementOf", { type: t(`backoffice:dataBank.agreementKinds.${agreement[1]}`, { defaultValue: agreement[1] }) });
    return t(`enums.transactionType.${x}`, { defaultValue: x });
  };
  const { locale } = useLocale();
  const navigate = useNavigate();
  const sectors = useSectors();
  const jurisdictions = useJurisdictions();
  const transactions = useTransactions();
  const tree = useMemo(() => {
    const all = sectors.data ?? [];
    const roots = all.filter((s) => !s.parentId);
    return roots.map((r) => ({ root: r, children: all.filter((s) => s.parentId === r.id) }));
  }, [sectors.data]);
  const jurisdictionGroups = useMemo(() => {
    const groups = new Map<string, typeof jurisdictions.data>();
    for (const j of jurisdictions.data ?? []) groups.set(j.kind, [...(groups.get(j.kind) ?? []), j]);
    return [...groups.entries()];
  }, [jurisdictions.data]);

  const SectorLine = ({ s, child }: { s: SectorRow; child?: boolean }) => (
    <div className={cn("flex items-start gap-3 py-1.5", child && "ps-6")}>
      <span className={cn("w-10 shrink-0 pt-0.5 font-mono text-xs", child ? "text-muted-foreground" : "font-semibold")}>{s.code}</span>
      <div className="min-w-0 flex-1">
        <p className={cn("text-sm", !child && "font-semibold")} dir="auto">{locale === "ar" ? s.nameAr : s.nameEn}{s.sensitive ? <Badge variant="warning" size="sm" className="ms-2 align-middle">{t("backoffice:dataBank.sensitive")}</Badge> : null}</p>
        {s.regulator ? <p className="text-[11px] text-muted-foreground">{authorityText(s.regulator, locale)}</p> : null}
      </div>
      <span className="tabular w-16 shrink-0 text-end text-xs text-muted-foreground">{s.entities} · {s.markets}</span>
    </div>
  );

  return (
    <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
      <Card className="min-w-0">
        <CardHeader className="pb-2"><CardTitle className="flex items-center gap-2 text-base"><Layers className="size-4 text-aegold-700" /> {t("backoffice:dataBank.sectors")}<InfoHint>{t("backoffice:dataBank.sectorsHint")}</InfoHint></CardTitle></CardHeader>
        <CardContent>
          {sectors.isLoading ? <Skeleton className="h-64" /> : (
            <div className="divide-y divide-sand-100">
              <div className="flex justify-end pb-1 text-[11px] uppercase tracking-wider text-muted-foreground">{t("backoffice:dataBank.entities")} · {t("backoffice:dataBank.markets")}</div>
              {tree.map(({ root, children }) => (
                <div key={root.id} className="py-1">
                  <SectorLine s={root} />
                  {children.map((c) => <div key={c.id}><SectorLine s={c} child />{c.notes ? <p className="ps-6 pb-1 text-[11px] text-muted-foreground"><SectorNote notes={c.notes} /></p> : null}</div>)}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="min-w-0 space-y-6">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="flex items-center gap-2 text-base"><Globe2 className="size-4 text-seablue-700" /> {t("backoffice:dataBank.jurisdictions")}<InfoHint>{t("backoffice:dataBank.jurisdictionsHint")}</InfoHint></CardTitle></CardHeader>
          <CardContent className="space-y-4">
            {jurisdictions.isLoading ? <Skeleton className="h-40" /> : jurisdictionGroups.map(([kind, items]) => (
              <div key={kind}>
                <p className="mb-1.5 text-[11px] uppercase tracking-wider text-muted-foreground">{t(`backoffice:dataBank.jurisdictionKinds.${kind}`, { defaultValue: kind })}</p>
                <div className="flex flex-wrap gap-1.5">
                  {(items ?? []).map((j) => <span key={j.id} title={`${j.competitionAuthority ?? ""}${j.notes ? ` — ${j.notes}` : ""}`} className="inline-flex items-center gap-1.5 rounded-full border border-sand-200 bg-white px-2.5 py-1 text-xs">{locale === "ar" ? j.nameAr : j.nameEn}</span>)}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2"><CardTitle className="flex items-center gap-2 text-base"><ArrowRightLeft className="size-4 text-camel-700" /> {t("backoffice:dataBank.transactions")} <Badge variant="outline" size="sm">{transactions.data?.length ?? 0}</Badge><InfoHint>{t("backoffice:dataBank.transactionsHint")}</InfoHint></CardTitle></CardHeader>
          <CardContent>
            {transactions.isLoading ? <Skeleton className="h-40" /> : (transactions.data ?? []).length === 0 ? <EmptyState icon={ArrowRightLeft} title={t("misc.noResults")} /> : (
              <div className="space-y-2">
                {transactions.data!.map((tx) => (
                  <button type="button" key={tx.id} disabled={!tx.caseId} onClick={() => tx.caseId && navigate(`/cases/${tx.caseId}`)} className="flex w-full items-start gap-3 rounded-xl border border-sand-200 p-3 text-start transition enabled:hover:border-aegold-300 enabled:hover:bg-aegold-50/40">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium"><SourceText text={tx.title} /></p>
                      <p className="text-xs text-muted-foreground">{transactionTypeLabel(tx.transactionType)} · <SourceText text={tx.partyNames.join(" · ")} /></p>
                      <p className="text-[11px] text-muted-foreground">{tx.caseNumber ? <span className="font-mono">{tx.caseNumber} · </span> : null}{tx.market ? (locale === "ar" && tx.marketAr ? tx.marketAr : <SourceText text={tx.market} />) : ""}{tx.announcedOn ? ` · ${formatDate(tx.announcedOn, locale)}` : ""}</p>
                    </div>
                    <div className="text-end"><p className="tabular text-sm font-semibold">{tx.valueAed ? `${fmtAed(tx.valueAed, locale)}` : "—"}</p>{tx.outcome ? <StatusBadge status={tx.outcome} label={OUTCOME_KEYS[tx.outcome] ? t(`backoffice:dataBank.outcomes.${OUTCOME_KEYS[tx.outcome]}`) : t(`decisionOutcomes.${tx.outcome}`, { defaultValue: t(`status.${tx.outcome}`, { defaultValue: tx.outcome }) })} /> : null}</div>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
